import {
  Model,
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { PublicEnquiryModel } from "../domain/PublicEnquiry";
import { MODULE_NAME } from "../moduleName";

import { dashboardService, publicEnquiryService } from "../services";
import {
  ActionPayload,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadedItems,
  LOADING_ITEM,
  LOADING_ITEMS,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  ADD_CASE_NOTE,
  addCaseNoteError,
  ADD_INTERNAL_MESSAGE,
  ASSIGNING_CASE,
  UNASSIGNING_CASE,
  CLOSING_CASE,
  REOPENING_CASE,
  PUBLIC_ENQUIRY_ANALYTICS_LOADED,
  PUBLIC_ENQUIRY_ANALYTICS_LOADING,
  PUBLIC_ENQUIRY_TEAM_ANALYTICS_LOADING,
  loadedTeamAnalytics,
  PENDING_LINES,
  loadingItem,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { DashboardTileModel } from "../presentation/dashboard/TileRenderers/types";
import { Attachment } from "../domain/Attachment";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}
function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchAddCaseNoteSaga() {
  yield takeLatest(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchAssignCaseHandlerSaga() {
  yield takeEvery(ASSIGNING_CASE, assignCaseHandlerSaga);
}
function* watchUnassignCaseHandlerSaga() {
  yield takeEvery(UNASSIGNING_CASE, unassignCaseHandlerSaga);
}

function* watchAddInternalMessage() {
  yield takeLatest(ADD_INTERNAL_MESSAGE, addInternalMessage);
}

function* watchCloseCaseSaga() {
  yield takeEvery(CLOSING_CASE, closeCaseSaga);
}
function* watchReopenCaseSaga() {
  yield takeEvery(REOPENING_CASE, reopenCaseSaga);
}
function* watchDashboardItemsSaga() {
  yield takeLatest(PUBLIC_ENQUIRY_ANALYTICS_LOADING, loadAnalytics);
}
function* watchTeamAnalyticsItemsSaga() {
  yield takeLatest(PUBLIC_ENQUIRY_TEAM_ANALYTICS_LOADING, loadTeamAnalytics);
}

function* watchPendingLinesSaga() {
  yield takeLatest(PENDING_LINES, setPendingLinesStatus);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<PublicEnquiryModel> = yield call(
    [publicEnquiryService, publicEnquiryService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const item: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.get],
    payload,
    {
      token: auth.token,
    }
  );
  yield put(loadedItem(item));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, patchWithMeta, documentsToDelete, newFiles } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<PublicEnquiryModel>;
    documentsToDelete: Attachment[];
    newFiles: File[];
  };

  const deleteAttachmentsCalls = () =>
    Promise.all(
      documentsToDelete.map((doc) =>
        publicEnquiryService.deleteSupportingDocument(id, doc.blobId, {
          token: auth.token,
        })
      )
    );

  const attachmentsCall = () => {
    newFiles && newFiles.length
      ? publicEnquiryService.postSupportingDocument(id, newFiles, {
          token: auth.token,
        })
      : Promise.resolve();
  };

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [publicEnquiryService, publicEnquiryService.update],
      id,
      patchWithMeta,
      {
        token: auth.token,
      }
    ),
    deletedDocs: call(deleteAttachmentsCalls),
    newDocs: call(attachmentsCall),
  });

  if (validatedResponse.isValid) {
    //yield put(savedItem(validatedResponse.data));
    yield put(loadingItem(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<PublicEnquiryModel>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const validatedResponse: ValidatedResponse<PublicEnquiryModel> = yield call(
    [publicEnquiryService, publicEnquiryService.create],
    payload,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    const id = validatedResponse.data.id;
    yield call(
      [publicEnquiryService, publicEnquiryService.postSupportingDocument],
      id,
      payload.supportingFiles,
      {
        token: auth.token,
      }
    );

    yield put({ type: CREATED_ITEM });
    yield call(() =>
      navigate(`/${MODULE_NAME}/list/${validatedResponse.data.id}`)
    );
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* addCaseNoteSaga({
  payload,
}: ActionPayload<{
  id: string;
  caseFiles: File[];
  caseNote: string;
  sla_ResponseType: number;
  subject: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, caseFiles, caseNote, sla_ResponseType, subject } = payload;
  //console.log("SagaPayload", payload);
  try {
    //console.log("Saga ", id, caseFiles, caseNote, payload);
    yield call(
      [publicEnquiryService, publicEnquiryService.addCaseNote],
      id,
      caseFiles,
      caseNote,
      sla_ResponseType,
      subject,
      { token }
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This enquiry has been closed"));
    } else {
      throw err;
    }
  }
}

function* addInternalMessage({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  subject: string;
  message: string;
  recipients: string[];
}>) {
  const { token } = yield select(authSelector);
  const { id, files, subject, message, recipients } = payload;
  //console.log("SagaPayload", payload);
  try {
    //console.log("Saga ", payload);
    yield call(
      [publicEnquiryService, publicEnquiryService.addInternalMessage],
      id,
      files,
      subject,
      message,
      recipients,
      { token }
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This enquiry has been closed"));
    } else {
      throw err;
    }
  }
}

function* assignCaseHandlerSaga({
  payload,
}: ActionPayload<{
  id: string;
  assignedToUpn: string;
  assignedToDisplayName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, assignedToUpn, assignedToDisplayName } = payload;

  const publicEnquiry: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.assign],
    id,
    assignedToUpn,
    assignedToDisplayName,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(publicEnquiry));
}
function* unassignCaseHandlerSaga({
  payload,
}: ActionPayload<{
  id: string;
}>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const publicEnquiry: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.unassign],
    id,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(publicEnquiry));
}

function* closeCaseSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id } = payload as {
    id: string;
  };

  const publicEnquiry: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.closeCase],
    id,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(publicEnquiry));
}
function* reopenCaseSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id } = payload as {
    id: string;
  };

  const publicEnquiry: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.reopenCase],
    id,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(publicEnquiry));
}

function* loadAnalytics({ payload }: ActionPayload<any>) {
  try {
    const { token } = yield select(authSelector);

    const { params } = payload;

    const response: DashboardTileModel = yield call(
      [dashboardService, dashboardService.getAnalytics],
      { ...params },
      {
        token,
      }
    );
    yield put({
      type: PUBLIC_ENQUIRY_ANALYTICS_LOADED,
      payload: response,
    });
  } catch (e) {
    throw e;
  }
}

function* loadTeamAnalytics() {
  try {
    const { token } = yield select(authSelector);
    const response: Model[] = yield call(
      [dashboardService, dashboardService.getTeamAnalytics],
      {
        token,
      }
    );
    yield put(loadedTeamAnalytics(response));
  } catch (e) {
    throw e;
  }
}

function* setPendingLinesStatus({
  payload,
}: ActionPayload<{ id: string; pendingLines: boolean }>) {
  const { id, pendingLines } = payload;
  const { token } = yield select(authSelector);

  const publicEnquiry: PublicEnquiryModel = yield call(
    [publicEnquiryService, publicEnquiryService.setPendingLines],
    id,
    pendingLines,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(publicEnquiry));
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchAddCaseNoteSaga(),
    watchAddInternalMessage(),
    watchAssignCaseHandlerSaga(),
    watchUnassignCaseHandlerSaga(),
    watchCloseCaseSaga(),
    watchReopenCaseSaga(),
    watchDashboardItemsSaga(),
    watchTeamAnalyticsItemsSaga(),
    watchPendingLinesSaga(),
    // further sagas...
  ]);
}
